import {
  ChannelType,
  canInGuild,
  emojiUrl,
  PermissionFlags,
  formatDiscordTimestamp,
  isJumboEmoji,
  type MarkdownNode,
  parseMarkdown,
  roleColorHex,
} from '@alternativamais/chat-core';
import { type ReactNode, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { useEstado } from '../chat/contexto';
import { useAjusteDaConta } from '../chat/ganchos';
import { usePreferencias } from '../chat/preferencias';
import { useNome } from '../chat/ganchos';
import { ARQUIVOS } from '../config';
import { Icone } from './Icone';

const MENCAO =
  'rounded px-[3px] font-medium bg-mencao-fundo text-mencao [overflow-wrap:anywhere]';

type Contexto = {
  /** Emoji animado anda (U7). */
  animar: boolean;
  /** Spoiler já aparece aberto (U7: sempre, ou nos canais que a pessoa gerencia). */
  spoilerAberto: boolean;
  guildId: string | null;
  jumbo: boolean;
};

/**
 * O markdown do Discord desenhado com a identidade nossa. A árvore vem do
 * core (`parseMarkdown`); aqui só a forma de cada nó.
 */
export function Markdown({
  texto,
  guildId,
  inline = false,
  umaLinha = false,
  className = '',
}: {
  texto: string;
  guildId: string | null;
  /** Sem blocos (título, citação, lista, bloco de código). */
  inline?: boolean;
  /** Numa linha só, cortada no fim (tópico no cabeçalho): quebras viram espaço. */
  umaLinha?: boolean;
  className?: string;
}) {
  const fonte = umaLinha ? texto.replace(/\s*\n+\s*/g, ' ') : texto;
  const nos = useMemo(
    () => parseMarkdown(fonte, { inline: inline || umaLinha }),
    [fonte, inline, umaLinha],
  );
  const jumbo = useMemo(
    () => !inline && !umaLinha && isJumboEmoji(nos),
    [nos, inline, umaLinha],
  );
  const animar = useAjusteDaConta('animate_emoji');
  const spoiler = usePreferencias((prefs) => prefs.spoiler);
  const gerencia = useEstado((estado) =>
    spoiler === 'gerencio' && guildId
      ? canInGuild(estado, guildId, PermissionFlags.MANAGE_MESSAGES)
      : false,
  );
  const contexto: Contexto = {
    guildId,
    jumbo,
    animar,
    spoilerAberto: spoiler === 'sempre' || gerencia,
  };
  return (
    <div
      className={`markdown min-w-0 ${
        umaLinha
          ? 'truncate whitespace-nowrap'
          : 'whitespace-pre-wrap [overflow-wrap:anywhere]'
      } ${jumbo ? 'text-[44px] leading-[1.2]' : ''} ${className}`}
    >
      {desenhar(nos, contexto)}
    </div>
  );
}

function desenhar(nos: MarkdownNode[], contexto: Contexto): ReactNode[] {
  return nos.map((no, indice) => (
    <No key={indice} no={no} contexto={contexto} />
  ));
}

function No({
  no,
  contexto,
}: {
  no: MarkdownNode;
  contexto: Contexto;
}): ReactNode {
  switch (no.type) {
    case 'text':
      return no.content;
    case 'br':
      return <br />;
    case 'strong':
      return (
        <strong className="font-bold">{desenhar(no.children, contexto)}</strong>
      );
    case 'em':
      return <em>{desenhar(no.children, contexto)}</em>;
    case 'underline':
      return <u>{desenhar(no.children, contexto)}</u>;
    case 'strike':
      return <s>{desenhar(no.children, contexto)}</s>;
    case 'spoiler':
      return (
        <Spoiler aberto={contexto.spoilerAberto}>
          {desenhar(no.children, contexto)}
        </Spoiler>
      );
    case 'inlineCode':
      return (
        <code className="rounded bg-codigo px-1 py-px font-mono text-[0.85em] text-codigo-texto [overflow-wrap:anywhere]">
          {no.content}
        </code>
      );
    case 'codeBlock':
      return (
        <pre className="my-1 max-w-[min(100%,720px)] overflow-x-auto rounded-lg border border-borda bg-codigo px-3 py-2.5 font-mono text-[13px] leading-normal whitespace-pre-wrap text-codigo-texto">
          <code>{no.content}</code>
        </pre>
      );
    case 'blockQuote':
      return (
        <blockquote className="my-0.5 flex gap-3">
          <span aria-hidden="true" className="w-1 shrink-0 rounded bg-ativo" />
          <span className="min-w-0">{desenhar(no.children, contexto)}</span>
        </blockquote>
      );
    case 'heading': {
      const tamanho = { 1: 'text-2xl', 2: 'text-xl', 3: 'text-base' }[no.level];
      return (
        <div
          role="heading"
          aria-level={no.level}
          className={`my-1 font-bold ${tamanho}`}
        >
          {desenhar(no.children, contexto)}
        </div>
      );
    }
    case 'subtext':
      return (
        <div className="text-[13px] text-texto-3">
          {desenhar(no.children, contexto)}
        </div>
      );
    case 'list': {
      const itens = no.items.map((item, indice) => (
        <li key={indice}>{desenhar(item, contexto)}</li>
      ));
      return no.ordered ? (
        <ol
          start={no.start}
          className="my-0.5 list-decimal pl-6 whitespace-normal"
        >
          {itens}
        </ol>
      ) : (
        <ul className="my-0.5 list-disc pl-6 whitespace-normal">{itens}</ul>
      );
    }
    case 'link':
      return (
        <a
          href={no.url}
          target="_blank"
          rel="noreferrer noopener"
          title={no.masked ? no.url : undefined}
          className="text-link hover:underline [overflow-wrap:anywhere]"
        >
          {desenhar(no.children, contexto)}
        </a>
      );
    case 'userMention':
      return <MencaoPessoa id={no.id} guildId={contexto.guildId} />;
    case 'roleMention':
      return <MencaoCargo id={no.id} guildId={contexto.guildId} />;
    case 'channelMention':
      return <MencaoCanal id={no.id} />;
    case 'everyone':
      return <span className={MENCAO}>@everyone</span>;
    case 'here':
      return <span className={MENCAO}>@here</span>;
    case 'customEmoji':
      return (
        <img
          src={emojiUrl(ARQUIVOS, {
            ...no,
            animated: no.animated && contexto.animar,
          })}
          alt={`:${no.name}:`}
          title={`:${no.name}:`}
          draggable={false}
          className="inline-block object-contain align-[-0.3em]"
          style={{
            width: contexto.jumbo ? 48 : 22,
            height: contexto.jumbo ? 48 : 22,
          }}
        />
      );
    case 'timestamp':
      return <Data unix={no.unix} estilo={no.style} />;
    case 'command':
      return <span className={MENCAO}>/{no.name}</span>;
    default:
      return null;
  }
}

function Spoiler({
  children,
  aberto,
}: {
  children: ReactNode;
  aberto: boolean;
}) {
  const [revelado, setRevelado] = useState(aberto);
  return (
    <span
      role="button"
      tabIndex={revelado ? -1 : 0}
      aria-label={revelado ? undefined : 'Spoiler: clique para mostrar'}
      aria-expanded={revelado}
      className={`spoiler ${revelado ? 'revelado' : ''}`}
      onClick={() => setRevelado(true)}
      onKeyDown={(evento) => {
        if (evento.key === 'Enter' || evento.key === ' ') setRevelado(true);
      }}
    >
      {children}
    </span>
  );
}

function MencaoPessoa({ id, guildId }: { id: string; guildId: string | null }) {
  const conhecida = useEstado((estado) => !!estado.users[id]);
  const nome = useNome(id, guildId);
  return <span className={MENCAO}>@{conhecida ? nome : 'desconhecido'}</span>;
}

function MencaoCargo({ id, guildId }: { id: string; guildId: string | null }) {
  const cargo = useEstado((estado) =>
    guildId ? estado.guilds[guildId]?.roles[id] : undefined,
  );
  if (!cargo) return <span className={MENCAO}>@cargo-desconhecido</span>;
  if (!cargo.color) return <span className={MENCAO}>@{cargo.name}</span>;
  const cor = roleColorHex(cargo.color);
  return (
    <span
      className="rounded px-[3px] font-medium"
      style={{ color: cor, background: `${cor}29` }}
    >
      @{cargo.name}
    </span>
  );
}

function MencaoCanal({ id }: { id: string }) {
  const canal = useEstado((estado) => estado.channels[id]);
  const navegar = useNavigate();
  if (!canal) {
    return (
      <span className={MENCAO}>
        <Icone nome="hash" tamanho={14} className="inline align-[-2px]" />
        canal-desconhecido
      </span>
    );
  }
  const thread =
    canal.type === ChannelType.PUBLIC_THREAD ||
    canal.type === ChannelType.PRIVATE_THREAD ||
    canal.type === ChannelType.ANNOUNCEMENT_THREAD;
  return (
    <button
      type="button"
      className={`${MENCAO} cursor-pointer hover:underline`}
      onClick={() => navegar(`/canais/${canal.guild_id ?? '@me'}/${canal.id}`)}
    >
      <Icone
        nome={thread ? 'thread' : 'hash'}
        tamanho={14}
        className="mr-0.5 inline align-[-2px]"
      />
      {canal.name}
    </button>
  );
}

function Data({
  unix,
  estilo,
}: {
  unix: number;
  estilo: Parameters<typeof formatDiscordTimestamp>[1];
}) {
  const completa = new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'full',
    timeStyle: 'short',
  }).format(new Date(unix * 1000));
  return (
    <time
      dateTime={new Date(unix * 1000).toISOString()}
      title={completa}
      className="rounded bg-ativo px-1"
    >
      {formatDiscordTimestamp(unix, estilo)}
    </time>
  );
}
